import type { BuilderCommunityMapConfig } from "api-types/builders/salesConfig";
import type { LotSpec, LotStatus } from "api-types/lots/lotTypes";
import { getLotBadgeDisplay, getLotBadgeSymbol } from "lib/components/builders/communities/map/lotBadge";
import {
  DELETE_MULTI_UNIT_HOME_PLAN_TOOLTIP,
  VIEW_SHOWROOM_FULL_BUILDING_MULTI_UNIT_TOOLTIP,
} from "lib/constants/messages";
import { homePlanShowroomPath } from "lib/routeHelpers";
import type { JSX } from "react";
import Link from "ui/components/Link";
import IconShowroom from "ui/icons/IconShowroom";
import IconTrash from "ui/icons/IconTrash";
import Tooltip from "ui/popovers/Tooltip";

export function getShowroomLinkText(
  homePlan: { isMultiUnit?: boolean; lockedAt?: Date | null },
  canEdit: boolean,
): string {
  if (homePlan.isMultiUnit) {
    return "View in Showroom";
  }
  if (!homePlan.lockedAt && canEdit) {
    return "Configure in Showroom";
  }
  return "View in Showroom";
}

export function isShowroomLinkDisabled(homePlan: { isMultiUnit?: boolean }): boolean {
  return !!homePlan.isMultiUnit;
}

export function getShowroomLinkTooltip(homePlan: { isMultiUnit?: boolean }): string | undefined {
  return isShowroomLinkDisabled(homePlan) ? VIEW_SHOWROOM_FULL_BUILDING_MULTI_UNIT_TOOLTIP : undefined;
}

// Rendered by both the Home Plan header menu (HomePlanLayout) and the table row menu
// (HomePlanDropdown), so the disabled state, tooltip, and label can't be changed in one menu and
// missed in the other — which is how the full-building Showroom block would regress.
// TODO(CFG-2519): the rest of those two menus is still declared twice and has already drifted;
// fold them into a shared menu builder rather than adding more one-off shared links here.
export function ShowroomMenuLink({
  builderId,
  homePlan,
  canEdit,
}: {
  builderId: string;
  homePlan: { id: string; isMultiUnit?: boolean; lockedAt?: Date | null };
  canEdit: boolean;
}): JSX.Element {
  return (
    <Link
      href={homePlanShowroomPath(builderId, homePlan.id)}
      disabled={isShowroomLinkDisabled(homePlan)}
      tooltip={getShowroomLinkTooltip(homePlan)}
    >
      <IconShowroom size={14} /> {getShowroomLinkText(homePlan, canEdit)}
    </Link>
  );
}

export function getHomePlanDeleteTooltip(homePlan: { isMultiUnit?: boolean }): string | undefined {
  return homePlan.isMultiUnit ? DELETE_MULTI_UNIT_HOME_PLAN_TOOLTIP : undefined;
}

// Used in HomePlanLayout, HomePlanDropdown, and PublicHomePlanDropdown so delete gating for
// building home plans can't drift between menus.
// TODO(CFG-2519): fold into a shared menu builder rather than adding more one-off shared links here.
export function DeleteHomePlanMenuLink({
  homePlan,
  onClick,
}: {
  homePlan: { isMultiUnit?: boolean };
  onClick: () => void;
}) {
  return (
    <Link disabled={homePlan.isMultiUnit} tooltip={getHomePlanDeleteTooltip(homePlan)} onClick={onClick}>
      <IconTrash size={14} />
      Delete Home Plan
    </Link>
  );
}

/**
 * Displays a lot status indicator dot with tooltip showing the status name.
 */
export function LotStatusTag({
  lotStatus,
  spec,
  communityMapConfig,
}: {
  lotStatus: LotStatus;
  spec: LotSpec;
  communityMapConfig?: BuilderCommunityMapConfig;
}): JSX.Element {
  const symbol = getLotBadgeSymbol({ lotStatus, spec });
  const { label, fill } = getLotBadgeDisplay(symbol, communityMapConfig);
  return (
    <Tooltip.Root>
      <Tooltip.Trigger>
        <span className="lot-status-tag">
          <span className="status-donut" style={{ backgroundColor: fill }} />
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content side="left">{label}</Tooltip.Content>
    </Tooltip.Root>
  );
}
